// Wire unions
export type MergeSection = 'auto_resolved' | 'needs_decision';
export type MergeSort = 'input_row_id' | 'row_status';
export type CellClass =
  'UNCHANGED' | 'MINE_ONLY' | 'THEIRS_ONLY' | 'BOTH_SAME' | 'CONFLICT';
export type RowStatus = 'CLEAN' | 'PARTIAL' | 'FULL_CONFLICT';
export type DecisionChoice = 'MINE' | 'THEIRS' | 'CUSTOM' | 'CLEAR';
export type RebaseOutcome =
  'REOPENED' | 'AUTO_REBASED' | 'DECISION_PRESERVED' | 'CONFLICT';
export type MissingSide = 'Base' | 'Mine' | 'Theirs';

// Typed only: the ROW_SET_MISMATCH message already names the rows, so
// nothing renders this.
export type RowSetMismatchRow = {
  Input_RowID: string;
  Tab_Name: string | null;
  MissingFrom: Array<'BASE' | 'MINE' | 'THEIRS'>;
  TabDiffers: boolean;
};

// The thirteen API codes, then four client-only codes (FORBIDDEN/NOT_FOUND map
// the gate's bare 403/404; SUBMIT_UNAVAILABLE = route not shipped;
// NETWORK = non-HTTP failure). GROUP_LOCKED is submit-only (409).
// RESOLUTION_UNAVAILABLE is a 409 on merge/submit: another user resolved (or
// is resolving) this version. Its body adds `State` and `ResolvedByName`.
export const MERGE_ERROR_CODES = [
  'INVALID_REQUEST',
  'INVALID_DECISION',
  'NO_LEGAL_DECISION',
  'ROW_SET_MISMATCH',
  'SUBMISSION_NOT_FOUND',
  'BASE_VERSION_UNKNOWN',
  'REBASE_REQUIRED',
  'CURRENT_VERSION_UNAVAILABLE',
  'PROMOTE_ORDER_UNAVAILABLE',
  'INTERNAL_ERROR',
  'GROUP_LOCKED',
  'RESOLUTION_UNAVAILABLE',
  'STAGING_MISMATCH',
] as const;
export const RESOLUTION_STATES = ['IN_PROGRESS', 'RESOLVED', 'FAILED'] as const;
export type ResolutionState = (typeof RESOLUTION_STATES)[number];
export type MergeApiErrorCode = (typeof MERGE_ERROR_CODES)[number];
export type MergeErrorCode =
  | MergeApiErrorCode
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'SUBMIT_UNAVAILABLE'
  | 'NETWORK'
  | 'ABORTED';
export const CELL_CLASSES = [
  'UNCHANGED',
  'MINE_ONLY',
  'THEIRS_ONLY',
  'BOTH_SAME',
  'CONFLICT',
] as const;

// DTOs
export type MergeDecisionDto = {
  input_row_id: string;
  date_id: string | null;
  choice: DecisionChoice;
  custom_value?: string;
};

export type MergeRequestBase = {
  GroupRowIdentifierID: number;
  MineSource: 'staging';
  SubmissionID: number;
};

export type MergeDiffRequest = MergeRequestBase;

export type MergeRowsFilter = {
  Tab_Name?: string;
  Input_Name?: string;
  ChangedUnderneath?: true;
};

// One entry of the rebase carrier, in `Cells[]`'s casing. The API schema is
// strict: both keys are required, nothing else is accepted, and DateID is
// null only for a single-value assumption.
export type ReopenedCellRef = {
  Input_RowID: string;
  DateID: string | null;
};

// Reopened (rows and counts): the last rebase's echo, sent back unchanged
// and only together with PreviousCurrentSubmissionID.
export type MergeRowsRequest = MergeRequestBase & {
  CurrentSubmissionID: number;
  Decisions: MergeDecisionDto[];
  Page: number; // 1-based
  PageSize: number; // ≤ 500
  Section: MergeSection;
  Sort: MergeSort;
  Filter?: MergeRowsFilter;
  PreviousCurrentSubmissionID?: number;
  Reopened?: ReopenedCellRef[];
};

export type MergeCountsRequest = MergeRequestBase & {
  CurrentSubmissionID: number;
  Decisions: MergeDecisionDto[];
  PreviousCurrentSubmissionID?: number;
  Reopened?: ReopenedCellRef[];
};

// Reopened: the last rebase's echo, or [] on a first rebase (which opts in to
// the echo). Never null.
export type MergeRebaseRequest = MergeRequestBase & {
  PreviousCurrentSubmissionID: number;
  Decisions: MergeDecisionDto[];
  Reopened?: ReopenedCellRef[];
};

// POST …/merge/submit (CLD-2971). Decisions are the ones the last rebase
// established; Reopened is that rebase's echo, omitted when it is empty.
// Returns 200 {Outcome}.
export type MergeSubmitRequest = MergeRequestBase & {
  CurrentSubmissionID: number;
  Decisions: MergeDecisionDto[];
  Reopened?: ReopenedCellRef[];
};

// DateID: null = single-value assumption; Resolved omitted when undecided,
// explicit null only when CLEARed; Outcome and NoDecisionNeeded present only
// when PreviousCurrentSubmissionID was sent. NoDecisionNeeded: a REOPENED cell
// in a row byte-identical on Base, Mine and Theirs; it asks no question, is
// not in Remaining, and a decision naming it is refused.
export type MergePeriodDto = {
  DateID: string | null;
  Class: CellClass;
  Base: string | null;
  Mine: string | null;
  Theirs: string | null;
  Missing: MissingSide[];
  Resolved?: string | null;
  Raw_Difference: string | null;
  Pct_Difference: string | null;
  Outcome?: RebaseOutcome | null;
  NoDecisionNeeded?: boolean;
};

// Input_Name/Input_Category arrive with the CLD-2970 additive API change
// (Task 69); absent before it.
export type MergeRowDto = {
  Input_RowID: string;
  Tab_Name: string | null;
  Dimensions: string[];
  RowStatus: RowStatus;
  Periods: MergePeriodDto[];
  Input_Name?: string | null;
  Input_Category?: string | null;
  Reopened?: boolean;
  DecisionPreserved?: boolean;
  AutoRebased?: boolean;
};

// Diff returns changed rows only, no periods.
export type MergeDiffRowDto = Pick<
  MergeRowDto,
  | 'Input_RowID'
  | 'Tab_Name'
  | 'Dimensions'
  | 'RowStatus'
  | 'Input_Name'
  | 'Input_Category'
>;

export type MergeByInputDto = {
  Input_Name: string;
  TotalConflicts: number;
  Resolved: number;
  Remaining: number;
  AutoResolved: number;
  Overrides: number;
  ChangedUnderneath: number;
};

// CurrentSubmissionID is present only on the flat merge/counts response,
// never inside diff/rebase Counts; PreviousCurrentSubmissionID is echoed only
// when it was sent; ByInput arrives with Task 69.
export type MergeCountsDto = {
  TotalConflicts: number;
  Resolved: number;
  Remaining: number;
  AutoResolved: number;
  Overrides: number;
  ChangedUnderneath: number;
  CurrentSubmissionID?: number;
  PreviousCurrentSubmissionID?: number;
  ByInput?: MergeByInputDto[];
};

export type MergeDiffResponse = {
  BaseSubmissionID: number;
  CurrentSubmissionID: number;
  Rows: MergeDiffRowDto[];
  Counts: MergeCountsDto;
};

export type PeriodBoundsDto = {
  From: string;
  To: string;
};

// Per-tab DateID spans from merge/rows (whole merge, independent of page/section/filter).
// From/To are DateID strings compared as integers.
export type TabPeriodSpanDto = {
  Tab_Name: string | null;
  Changed: PeriodBoundsDto;
  NeedsDecision: PeriodBoundsDto | null;
  ChangesOutsideNeedsDecision: number | null;
};

export type MergePageDto = {
  Page: number;
  PageSize: number;
  TotalRows: number;
  CurrentSubmissionID: number;
  PreviousCurrentSubmissionID?: number;
  Rows: MergeRowDto[];
  // Optional: an older API omits it.
  PeriodSpans?: TabPeriodSpanDto[];
};

// NoDecisionNeeded: as on MergePeriodDto (true only on a REOPENED cell).
export type MergeRebaseCellDto = {
  DateID: string | null;
  Outcome: RebaseOutcome;
  Section: MergeSection;
  NoDecisionNeeded?: boolean;
};

export type MergeRebaseRowDto = MergeRowDto & {
  Cells: MergeRebaseCellDto[];
};

// Decisions are re-expressed against the new current; resend these.
// Reopened is the carrier to send next (every REOPENED cell, NoDecisionNeeded
// ones included), present only when the request sent Reopened.
export type MergeRebaseResponse = {
  BaseSubmissionID: number;
  PreviousCurrentSubmissionID: number;
  NewCurrentSubmissionID: number;
  Rows: MergeRebaseRowDto[];
  Decisions: MergeDecisionDto[];
  Counts: MergeCountsDto;
  Reopened?: ReopenedCellRef[];
};

export type MergeSubmitOutcome = 'RECALCULATE_QUEUED' | 'PROMOTED' | 'FAILED';

export type MergeSubmitResponse = {
  Outcome: MergeSubmitOutcome;
};

// UI-only types (not transport)
export type DisplayTone = 'grey' | 'blue' | 'brown' | 'red';

export type RowDecisionStatus =
  'PENDING' | 'CHOSE_MINE' | 'CHOSE_THEIRS' | 'CHOSE_MIXED' | 'AUTO_RESOLVED';

// Phase 2, kept for the fixtures.
export type DownstreamImpact = {
  calculatedValue: string;
  dependsOn: string[];
  impactLevel: 'Partial' | 'High';
};
